"use client";

import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const manifestoItems = [{
  question: "How do you audit my current revenue performance?",
  answer: "We conduct a deep-dive analysis of your historical data, OTA performance, and market positioning. Our audit identifies missed opportunities in your pricing strategy and tech stack, providing a clear roadmap for revenue growth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png",
  imageAlt: "Data analyst reviewing hotel revenue charts"
}, {
  question: "What is your approach to OTA management?",
  answer: "We optimize your listings with high-converting content, strategic rate management, and visibility tactics. Our goal is to maximize your presence on platforms like Booking.com and Expedia while driving direct bookings.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_optimization.webp",
  imageAlt: "OTA dashboard showing booking performance"
}, {
  question: "How do you recommend hospitality tech stacks?",
  answer: "We evaluate your current systems and recommend integrated solutions—from PMS to Channel Managers—that streamline operations and improve data accuracy, ensuring your tech stack supports your revenue goals.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp",
  imageAlt: "Hotel management software interface on laptop"
}, {
  question: "Can you help with direct booking growth?",
  answer: "Yes, we implement conversion-focused website strategies and digital marketing campaigns designed to shift bookings from high-commission OTAs to your own direct booking engine, increasing your net revenue.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_growth.png",
  imageAlt: "Direct booking conversion metrics on screen"
}, {
  question: "What is the typical timeline for results?",
  answer: "While immediate improvements in OTA visibility can be seen within weeks, comprehensive revenue growth strategies typically show significant impact on RevPAR and GOPPAR within 3 to 6 months of implementation.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_growth_timeline.webp",
  imageAlt: "Growth chart showing upward revenue trend"
}, {
  question: "Do you work with independent hotels?",
  answer: "Absolutely. We specialize in helping independent hotels, boutique properties, and small chains compete effectively against larger brands by leveraging data-driven revenue management and smart digital strategies.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/independent_hotel_consulting.webp",
  imageAlt: "Boutique hotel exterior at sunset"
}, {
  question: "How is your consulting fee structured?",
  answer: "We offer flexible engagement models, including performance-based retainers and project-based audits. Our goal is to ensure our services provide a clear, positive ROI for your property.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/consulting_fee_structure.webp",
  imageAlt: "Professional contract document on desk"
}, {
  question: "How do I get started with HotelRevBaBa?",
  answer: "Simply book a consultation through our website. We will schedule an initial discovery call to understand your property's unique challenges and discuss how our revenue management expertise can help you succeed.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/consultation_booking.png",
  imageAlt: "Sunil Singh discussing strategy with hotel owner"
}];
export default function () {
  const [openValue, setOpenValue] = useState<string>("item-0");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1664" id="hiringprocessfaq" className="relative bg-slate-50 text-slate-900 py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 lg:px-22">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 relative">
            <div ref={headerRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-10 -left-4 lg:-left-8 select-none font-default font-light leading-none text-blue-900/5" style={{
              fontSize: "20vw"
            }}>
                05
              </div>

              {}
              <div className="relative flex items-center gap-3 mb-8">
                <span className="font-default text-xs uppercase tracking-[0.18em] text-blue-600">
                  § 05 / CONSULTING — FAQ
                </span>
              </div>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-blue-600" />
                <div className="h-1 w-1 bg-blue-600" />
              </div>

              {}
              <h2 className="relative font-default font-bold leading-[1.0] tracking-tight text-slate-900 mb-8" style={{
              fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
            }}>
                Revenue Management{" "}
                <span className="italic text-blue-600">Insights</span>
              </h2>

              {}
              <p className="relative font-default text-slate-700 leading-relaxed mb-10 max-w-md" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
            }}>
                Expert answers to your most common hospitality revenue questions.
              </p>

              {}
              <Link to="/ContactPage" className="relative inline-flex items-center gap-2 font-default text-xs uppercase tracking-[0.16em] text-blue-600 group">
                <span className="relative">
                  Book a Consultation
                  <span className="absolute left-0 -bottom-1 h-px bg-blue-600 transition-all duration-300 w-[60%] group-hover:w-full" />
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible value={openValue} onValueChange={v => setOpenValue(v)} className="w-full">
              {manifestoItems.map((item, i) => {
              const itemValue = `item-${i}`;
              const isOpen = openValue === itemValue;
              const indexLabel = `${String(i + 1).padStart(2, "0")} / ${String(manifestoItems.length).padStart(2, "0")}`;
              return <AccordionItem key={i} data-index={i} value={itemValue} className="relative border-b-0 border-t border-slate-200">
                    {}
                    <span aria-hidden="true" className={`pointer-events-none absolute left-0 top-0 w-[2px] bg-blue-600 transition-all duration-300 ease-out ${isOpen ? "h-full opacity-100" : "h-0 opacity-0"}`} />

                    <AccordionTrigger className={`group flex items-center justify-between gap-6 py-6 hover:no-underline rounded-md transition-colors duration-300 [&>svg]:hidden ${isOpen ? "text-slate-900" : "text-slate-700"}`}>
                      <span className="flex-1 text-left font-default font-semibold leading-tight tracking-tight" style={{
                    fontSize: "clamp(1.125rem, 1.8vw, 1.5rem)"
                  }}>
                        {item.question}
                      </span>
                      <span className="hidden sm:block font-default text-xs uppercase tracking-[0.18em] text-slate-400 shrink-0">
                        {indexLabel}
                      </span>
                      <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 text-blue-600 transition-all duration-300 ease-out ${isOpen ? "rotate-45 opacity-100" : "rotate-0 opacity-70 group-hover:opacity-100"}`} aria-hidden="true">
                        <Plus className="h-5 w-5" strokeWidth={1.25} />
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="pb-8 pt-2 pl-0 md:pl-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        <p className="md:col-span-2 font-default text-slate-700 leading-relaxed" style={{
                      fontSize: "0.9375rem",
                      lineHeight: 1.7
                    }}>
                          {item.answer}
                        </p>

                        {}
                        <div className="hidden md:block relative">
                          <div className="relative border border-slate-200 aspect-[4/5] overflow-hidden rounded-lg shadow-md">
                            {}
                            <span aria-hidden="true" className="absolute top-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-blue-600" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-blue-600" />
                            </span>
                            <span aria-hidden="true" className="absolute top-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-blue-600" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-blue-600" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-blue-600" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-blue-600" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-blue-600" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-blue-600" />
                            </span>

                            <img src={item.image} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]" />
                          </div>
                          <div className="mt-3 font-default text-[0.7rem] uppercase tracking-[0.18em] text-slate-400">
                            FIG. {String(i + 1).padStart(2, "0")} — DETAIL
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}

              {}
              <div className="relative border-t border-slate-200 pt-6 flex items-center gap-2">
                <div className="h-1 w-1 bg-blue-600" />
                <div className="h-px w-16 bg-blue-600" />
                <span className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-slate-400 ml-2">
                  End of insights
                </span>
              </div>
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}